import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/customer-ui/components/dialog";
import { OptionContent } from "@babackapp/customer-ui/components/option";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/customer-ui/components/select";
import { cn } from "@babackapp/customer-ui/lib/utils";
import { Loader2Icon } from "lucide-react";
import {
  type ComponentProps,
  type SyntheticEvent,
  Suspense,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import {
  type OptionChoice,
  useVariantSelector,
} from "../../hooks/use-variant-selector";

type VariantFooterProps = {
  orderId: string;
  lineItemId: string;
  shopId: string;
  onVariantChange: (variantId: string | null) => void;
  /** Variant already in the draft, kept when the customer comes back. */
  exchangeVariantId: string | null;
};

type PreviewStore = {
  get: () => string | undefined;
  set: (url: string | undefined) => void;
  subscribe: (listener: () => void) => () => void;
};

/**
 * Hover preview kept out of React state: a footer re-render while a select is
 * open makes Radix reposition its content, which snaps the list scroll and
 * loses the keyboard focus in long lists. Only the thumbnail subscribes.
 */
function createPreviewStore(): PreviewStore {
  let url: string | undefined;
  const listeners = new Set<() => void>();
  return {
    get: () => url,
    set: (next) => {
      if (next === url) return;
      url = next;
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * Thumbnail of the selected variant, or of the item hovered in an open select.
 * The spinner mounts only while loading and fades in past a short delay
 * (`starting:` runs the transition on mount too), so cached images don't flash it.
 * Rendered as the dialog trigger, so closing the zoom gives the focus back.
 */
function VariantThumbnail({
  preview,
  selectedImageUrl,
  ...buttonProps
}: {
  preview: PreviewStore;
  selectedImageUrl: string | undefined;
} & ComponentProps<"button">) {
  const previewUrl = useSyncExternalStore(preview.subscribe, preview.get);
  // A hovered product without image keeps the selection: no layout shift.
  const src = previewUrl || selectedImageUrl;
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const isLoading = loadedSrc !== src;

  if (!src) return null;

  const settle = (e: SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.currentSrc === e.currentTarget.src) setLoadedSrc(src);
  };

  return (
    <button
      type="button"
      {...buttonProps}
      className="relative size-12 shrink-0 cursor-zoom-in overflow-hidden rounded-md bg-muted outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <img
        src={src}
        alt=""
        // A load or error queued for the previous image can run after `src`
        // changed: only the request for the current `src` ends the spinner.
        onLoad={settle}
        onError={settle}
        className="size-full object-cover"
      />
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center bg-muted/70 opacity-100 transition-opacity delay-150 starting:opacity-0">
          <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
        </span>
      )}
    </button>
  );
}

type ChoiceSelectProps = {
  value: string;
  choices: (OptionChoice & { label?: string })[];
  locked: boolean;
  onChange: (value: string) => void;
  onPreview: (imageUrl: string | undefined) => void;
};

/**
 * One select of the picker. Highlighting an item focuses it (pointer and
 * keyboard alike), so `onFocus` drives the thumbnail preview; the current
 * value, leaving an item or closing falls back to the selection.
 *
 * A locked select stays focusable (`aria-disabled`, never opens) instead of
 * `disabled`: picking the last reachable value locks it in the same commit,
 * and Radix would hand the focus back to a disabled trigger, i.e. to `<body>`.
 * A select locked while open (shop refetch) is closed by the `open` prop alone,
 * which Radix does not report through `onOpenChange`: the open state is reset
 * here so it does not reopen by itself once unlocked, and the preview is
 * cleared on any close.
 */
function ChoiceSelect({
  value,
  choices,
  locked,
  onChange,
  onPreview,
}: ChoiceSelectProps) {
  const t = useCustomText();
  const [isOpen, setIsOpen] = useState(false);
  if (isOpen && locked) setIsOpen(false);
  const open = isOpen && !locked;
  useEffect(() => {
    if (open) return () => onPreview(undefined);
  }, [open, onPreview]);
  return (
    <Select
      value={value}
      // Inside a form, Radix syncs a hidden native select that reports "" when
      // the value is not among its previous options (product switch): ignored.
      onValueChange={(v) => {
        if (v) onChange(v);
      }}
      open={open}
      onOpenChange={(next) => setIsOpen(next && !locked)}
    >
      <SelectTrigger
        aria-disabled={locked || undefined}
        className={cn("w-auto", locked && "cursor-not-allowed opacity-50")}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {choices.map((c) => (
          <SelectItem
            key={c.value}
            value={c.value}
            disabled={!c.available}
            onFocus={() =>
              onPreview(c.value === value ? undefined : c.imageUrl)
            }
            onBlur={() => onPreview(undefined)}
          >
            {c.label ?? c.value}
            {!c.available && ` (${t("exchangeOptions.variant.unavailable")})`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function VariantFooterInner({
  orderId,
  lineItemId,
  shopId,
  onVariantChange,
  exchangeVariantId,
}: VariantFooterProps) {
  const {
    productOption,
    selectedProductId,
    selectedOptions,
    selectedProduct,
    selectedImageUrl,
    selectableOptions,
    hasRealChoice,
    handleProductChange,
    handleOptionChange,
  } = useVariantSelector(
    orderId,
    lineItemId,
    shopId,
    onVariantChange,
    exchangeVariantId,
  );
  const t = useCustomText();
  const [preview] = useState(createPreviewStore);

  if (!hasRealChoice) return null;

  const productTitle = selectedProduct?.productTitle ?? "";

  return (
    <OptionContent className="flex-wrap items-center">
      <Dialog>
        <DialogTrigger asChild>
          <VariantThumbnail
            preview={preview}
            selectedImageUrl={selectedImageUrl}
            aria-label={t("exchangeOptions.variant.zoomImage", {
              product: productTitle,
            })}
          />
        </DialogTrigger>
        {/* No corner X over the photo: the image itself closes the dialog,
            like the overlay and Escape. */}
        <DialogContent
          className="max-w-lg p-2"
          hideCloseButton
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">{productTitle}</DialogTitle>
          {selectedImageUrl && (
            <DialogClose
              className="cursor-zoom-out"
              aria-label={t("common.close")}
            >
              <img
                src={selectedImageUrl}
                alt={productTitle}
                className="w-full rounded-xl object-contain"
              />
            </DialogClose>
          )}
        </DialogContent>
      </Dialog>
      {productOption && (
        <ChoiceSelect
          value={selectedProductId}
          choices={productOption.choices}
          locked={productOption.locked}
          onChange={handleProductChange}
          onPreview={preview.set}
        />
      )}
      {selectableOptions.map((opt) => (
        <ChoiceSelect
          // Remounted per product: a select reused across products would
          // carry the previous product's values (see onValueChange above).
          key={`${selectedProductId}:${opt.name}`}
          value={selectedOptions[opt.name] ?? ""}
          choices={opt.choices}
          locked={opt.locked}
          onChange={(v) => handleOptionChange(opt.name, v)}
          onPreview={preview.set}
        />
      ))}
    </OptionContent>
  );
}

export function VariantFooter(props: VariantFooterProps) {
  return (
    <Suspense fallback={null}>
      <VariantFooterInner {...props} />
    </Suspense>
  );
}
